import { envConfig } from "@configs/env";
import { CURRENCY_CONFIGS, LANGUAGES_MAP, STORAGE_KEY } from "@constants";
import { DEFAULT_PHONE_CONFIG, PHONE_CONFIGS } from "@constants/phone";
import { store } from "@stores";
import {
  Currency,
  CurrencyDirection,
  DeployCountry,
  Language,
  MemberSettings,
  _MemberSettingOnboardDialogLayout,
} from "@types";
import { getValidLanguage } from "@utils/locales";
import {
  InfoResponse,
  MemberSettingAuthMode,
  MemberSettingDepositMethodGrouping,
  MemberSettingDownloadBannerAction,
  MemberSettingFundingMode,
  MemberSettingNameMode,
  MemberSettingPlayerNameCollectionMode,
} from "kz-ui-sdk";
import { getDevFlags } from "./dev-flags";
import logger from "./logger";

const devFlags = getDevFlags();

const DIALOG_LAYOUT_DEV = devFlags?.dialogLayout;
const DEPOSIT_MODE_DEV = devFlags?.depositMode;
const NAME_MODE_DEV = devFlags?.nameMode;
const FUNDING_MODE_DEV = devFlags?.fundingMode;
const OTP_TYPE_DEV = devFlags?.otpType;
const NAME_COLLECTION_MODE_DEV = devFlags?.nameCollectionMode;
const AUTH_MODE_DEV = devFlags?.authMode;

// At first hour after deployment, /info may be cached in browser, so we need to fallback to default hardcoded settings first
const FALLBACK_MEMBER_SETTINGS: Record<
  DeployCountry,
  Pick<
    MemberSettings,
    | "locales"
    | "language"
    | "authMode"
    | "fundingMode"
    | "nameMode"
    | "onboardDialogLayout"
    | "depositMode"
    | "otpType"
    | "nameCollectionMode"
  >
> = {
  [DeployCountry.PH]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN], LANGUAGES_MAP[Language.TL]],
    language: Language.TL,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "SMS",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.TH]: {
    authMode: MemberSettingAuthMode.PhonePin,
    fundingMode: MemberSettingFundingMode.BankOnly,
    nameMode: MemberSettingNameMode.FirstLast,
    locales: [LANGUAGES_MAP[Language.EN], LANGUAGES_MAP[Language.TH]],
    language: Language.TH,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_DRAWER,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "SMS",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.ID]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN], LANGUAGES_MAP[Language.ID]],
    language: Language.ID,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "WhatsApp",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.BD]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN], LANGUAGES_MAP[Language.BN]],
    language: Language.BN,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "WhatsApp",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.PK]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN]],
    language: Language.EN,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "WhatsApp",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },

  [DeployCountry.MX]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN]],
    language: Language.EN,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "SMS",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.IN]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN]],
    language: Language.EN,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "SMS",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
  [DeployCountry.BR]: {
    authMode: MemberSettingAuthMode.UsernamePassword,
    fundingMode: MemberSettingFundingMode.EWalletOnly,
    nameMode: MemberSettingNameMode.FullInFirst,
    locales: [LANGUAGES_MAP[Language.EN]],
    language: Language.EN,
    onboardDialogLayout: _MemberSettingOnboardDialogLayout.DIALOG_PAGE,
    depositMode: MemberSettingDepositMethodGrouping.Individual,
    otpType: "SMS",
    nameCollectionMode: MemberSettingPlayerNameCollectionMode.Registration,
  },
};

// fallback member settings mode
const fallbackMemberSettings = FALLBACK_MEMBER_SETTINGS[envConfig.countryCode as DeployCountry];

const DEFAULT_MEMBER_SETTINGS: MemberSettings = {
  themeId: import.meta.env.VITE_DEV_THEME_PRESET || import.meta.env.VITE_THEME_PRESET,
  phonePrefix: "",
  phoneLengths: [0],
  phoneMinLength: 0,
  phoneMaxLength: 0,
  currency: "",
  currencyDirection: CurrencyDirection.SUFFIX,
  currencySymbol: "",
  language: fallbackMemberSettings.language,
  locales: fallbackMemberSettings.locales,
  countryCode: "",
  authMode: fallbackMemberSettings.authMode,
  fundingMode: fallbackMemberSettings.fundingMode,
  nameMode: fallbackMemberSettings.nameMode,
  onboardDialogLayout: fallbackMemberSettings.onboardDialogLayout,
  depositMode: fallbackMemberSettings.depositMode,
  otpType: fallbackMemberSettings.otpType,
  isLoaded: false,
  phoneStartWith: DEFAULT_PHONE_CONFIG.startWith,
  downloadBanner: {
    action: MemberSettingDownloadBannerAction.DownloadPwa,
  },
  nameCollectionMode: fallbackMemberSettings.nameCollectionMode,
  googleAuthEnabled: false,
  isSupportAuthSocial: false,
};

/**
 * Build & persist member settings from server info
 */
export const buildMemberSettings = (serverInfo: InfoResponse): MemberSettings => {
  // currency config
  const currencyCode = serverInfo?.currency ?? DEFAULT_MEMBER_SETTINGS.currency;
  let currencyConfig = null;
  if (currencyCode) {
    currencyConfig = CURRENCY_CONFIGS[currencyCode as Currency];
  }

  // phone configs:
  // length: add 1 for the leading 0
  const phoneLengths = serverInfo?.phoneSpec?.lengthRange;
  const phonePrefix = serverInfo?.phoneSpec?.phonePrefix;
  const phoneConfig = phonePrefix ? PHONE_CONFIGS[phonePrefix] : undefined;

  const phoneLegacyPhoneLength = serverInfo?.phoneSpec?.length ?? 0; // just fallback if phoneLengths is not available
  const safePhoneLengths = !!phoneLengths && phoneLengths.length > 0 ? phoneLengths : [phoneLegacyPhoneLength];

  const refinedPhoneLengths =
    phoneConfig && phoneConfig.additionalLengths
      ? [...safePhoneLengths, ...phoneConfig.additionalLengths]
      : safePhoneLengths;

  let phoneStartWith = DEFAULT_PHONE_CONFIG.startWith;
  const addLeadingOffset = phoneConfig?.noLeadingZero ? 0 : 1;
  const phoneMinLength = Math.min(...refinedPhoneLengths) + addLeadingOffset;
  const phoneMaxLength = Math.max(...refinedPhoneLengths) + addLeadingOffset;

  let phoneSimpleError;
  let showPhoneInputPrefix;

  if (phoneConfig) {
    phoneStartWith = phoneConfig.startWith;
    phoneSimpleError = phoneConfig.simpleError;
    showPhoneInputPrefix = phoneConfig.displayInputPrefix;
  }

  const defaultLanguage = serverInfo?.locales?.find((locale) => locale.isDefault)?.code;

  const isSupportAuthSocial =
    [serverInfo?.memberSettings?.googleAuth].some((enabled) => enabled) ?? DEFAULT_MEMBER_SETTINGS.isSupportAuthSocial;

  const memberSettings: MemberSettings = {
    themeId: serverInfo?.theme?.color ?? DEFAULT_MEMBER_SETTINGS.themeId,
    // phone config
    phonePrefix: phonePrefix ?? DEFAULT_MEMBER_SETTINGS.phonePrefix,
    phoneLengths: refinedPhoneLengths,
    phoneMinLength,
    phoneMaxLength,
    phoneStartWith,
    phoneSimpleError,
    // language config
    language: defaultLanguage || serverInfo?.languageCode || DEFAULT_MEMBER_SETTINGS.language,
    locales:
      serverInfo?.locales?.slice()?.sort((a, b) => (a?.sortPriority ?? 0) - (b?.sortPriority ?? 0)) ??
      DEFAULT_MEMBER_SETTINGS.locales,
    // currency config
    currency: currencyCode as Currency,
    currencyDirection: currencyConfig?.direction ?? DEFAULT_MEMBER_SETTINGS.currencyDirection,
    currencySymbol: currencyConfig?.symbol ?? DEFAULT_MEMBER_SETTINGS.currencySymbol,
    // country config
    countryCode: serverInfo?.countryCode ?? DEFAULT_MEMBER_SETTINGS.countryCode,
    // feature flags
    authMode: AUTH_MODE_DEV ?? serverInfo?.memberSettings?.authMode ?? DEFAULT_MEMBER_SETTINGS.authMode,
    fundingMode: FUNDING_MODE_DEV ?? serverInfo?.memberSettings?.fundingMode ?? DEFAULT_MEMBER_SETTINGS.fundingMode,
    nameMode: NAME_MODE_DEV ?? serverInfo?.memberSettings?.nameMode ?? DEFAULT_MEMBER_SETTINGS.nameMode,
    onboardDialogLayout: DIALOG_LAYOUT_DEV || DEFAULT_MEMBER_SETTINGS.onboardDialogLayout,
    otpType: OTP_TYPE_DEV || DEFAULT_MEMBER_SETTINGS.otpType,
    depositMode:
      DEPOSIT_MODE_DEV || serverInfo?.memberSettings?.depositMethodGrouping || DEFAULT_MEMBER_SETTINGS.depositMode,
    isLoaded: !!serverInfo,
    downloadBanner: serverInfo?.theme?.downloadBanner ?? DEFAULT_MEMBER_SETTINGS.downloadBanner,

    nameCollectionMode:
      NAME_COLLECTION_MODE_DEV ??
      serverInfo?.memberSettings?.playerNameCollectionMode ??
      DEFAULT_MEMBER_SETTINGS.nameCollectionMode,
    showPhoneInputPrefix,
    playerInfoCollections: serverInfo?.memberSettings?.playerInfoCollections ?? [],
    googleAuthEnabled: serverInfo?.memberSettings?.googleAuth ?? DEFAULT_MEMBER_SETTINGS.googleAuthEnabled,
    isSupportAuthSocial: isSupportAuthSocial ?? DEFAULT_MEMBER_SETTINGS.isSupportAuthSocial,
  };

  // If language is not in locales, use default language from list or fallback to first language from locales or fallback to default language
  if (!memberSettings.locales?.some((locale) => locale.code === memberSettings.language)) {
    memberSettings.language =
      getValidLanguage(
        memberSettings.locales ?? [],
        memberSettings.language as Language,
        DEFAULT_MEMBER_SETTINGS.language as Language,
      ) ?? "";
  }

  // override themeId from dev environment
  const devThemePreset = import.meta.env.VITE_DEV_THEME_PRESET;
  if (devThemePreset) {
    memberSettings.themeId = devThemePreset;
  }

  persistMemberSettings(memberSettings);
  return memberSettings;
};

export const persistMemberSettings = (memberSettings: MemberSettings) => {
  localStorage.setItem(STORAGE_KEY.MEMBER_SETTINGS, JSON.stringify(memberSettings));
};

/**
 * Get member settings from local storage
 * Use for internal use, fallback to default settings if not found
 * @returns MemberSettings
 */
export const getStoredMemberSettings = (): MemberSettings => {
  const storedSettings = localStorage.getItem(STORAGE_KEY.MEMBER_SETTINGS);
  if (storedSettings) {
    return JSON.parse(storedSettings) as MemberSettings;
  }
  return DEFAULT_MEMBER_SETTINGS;
};

/**
 * Get member settings from Redux state
 * @returns MemberSettings
 */
export const getReduxMemberSettings = (): MemberSettings | null => {
  try {
    if (store && store.getState) {
      const state = store.getState();
      // Check if preferences slice exists in state
      if (state && state.preferences && state.preferences.memberSettings) {
        return state.preferences.memberSettings;
      }
    }
    logger._console.error("Failed to get member settings from Redux state, fallback to local storage");
    return null;
  } catch (error) {
    logger._console.error("Failed to get member settings from Redux state, fallback to local storage");
    return null;
  }
};

/**
 * Get member settings from Redux state or local storage
 * @returns MemberSettings
 */
export const getMemberSettings = (): MemberSettings => {
  return getReduxMemberSettings() || getStoredMemberSettings();
};
